import { FC, useMemo, useCallback, memo } from 'react';

import clsx from 'clsx';
import ReactPaginate from 'react-paginate';

import styles from './styles.module.scss';

import LeftAngleIcon from '@/assets/icons/left-angle.svg?react';
import RightAngleIcon from '@/assets/icons/right-angle.svg?react';

interface PaginationProps {
  totalItems: number;
  pageSize: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  disabled?: boolean;
}

export const Pagination: FC<PaginationProps> = memo(
  ({ totalItems, pageSize, currentPage, setCurrentPage, disabled = false }) => {
    const totalPages = useMemo(() => Math.ceil(totalItems / pageSize), [totalItems, pageSize]);

    const { fromItem, toItem } = useMemo(
      () => ({
        fromItem: currentPage * pageSize + 1,
        toItem: Math.min((currentPage + 1) * pageSize, totalItems),
      }),
      [currentPage, pageSize, totalItems],
    );

    const handlePageChange = useCallback(
      ({ selected: page }: { selected: number }) => {
        if (page < 0 || page >= totalPages || disabled) {
          return;
        }

        setCurrentPage(page);
      },
      [totalPages, disabled, setCurrentPage],
    );

    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className={styles.wrapper}>
        <ReactPaginate
          className={clsx(styles.pagination, { [styles.disabled]: disabled })}
          forcePage={currentPage}
          pageCount={totalPages}
          pageRangeDisplayed={3}
          marginPagesDisplayed={2}
          previousLabel={<LeftAngleIcon className={styles.arrowIcon} />}
          nextLabel={<RightAngleIcon className={styles.arrowIcon} />}
          breakLabel="..."
          onPageChange={handlePageChange}
          renderOnZeroPageCount={null}
          pageClassName={styles.page}
          breakClassName={styles.page}
          previousClassName={styles.nav}
          nextClassName={styles.nav}
          activeClassName={styles.active}
          disabledClassName={styles.disabledNav}
        />
        <p className={styles.info}>{`${fromItem}-${toItem} of ${totalItems} Results`}</p>
      </div>
    );
  },
);

Pagination.displayName = 'Pagination';
